import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Public pages
import LandingPage from './screens/public/LandingPage';
import HowItWorksPage from './screens/public/HowItWorksPage';
import FeaturesPage from './screens/public/FeaturesPage';
import FAQPage from './screens/public/FAQPage';
import PrivacyPage from './screens/public/PrivacyPage';
import TermsPage from './screens/public/TermsPage';
import AboutPage from './screens/public/AboutPage';
import ContactPage from './screens/public/ContactPage';
import CityPage from './screens/public/CityPage';
import CuisinePage from './screens/public/CuisinePage';
import LocationsPage from './screens/public/LocationsPage';
import WhereShouldIEatPage from './screens/public/WhereShouldIEatPage';
import CouplesRestaurantPickerPage from './screens/public/CouplesRestaurantPickerPage';
import GroupRestaurantDecisionPage from './screens/public/GroupRestaurantDecisionPage';
import RestaurantWheelAlternativePage from './screens/public/RestaurantWheelAlternativePage';
import FoodDecisionFatiguePage from './screens/public/FoodDecisionFatiguePage';
import RandomRestaurantPickerPage from './screens/public/RandomRestaurantPickerPage';
import DateNightRestaurantsPage from './screens/public/DateNightRestaurantsPage';
import BestRestaurantsNearMePage from './screens/public/BestRestaurantsNearMePage';
import WhatToEatTonightPage from './screens/public/WhatToEatTonightPage';
import TryItFreePage from './screens/public/TryItFreePage';
import PricingPage from './screens/public/PricingPage';
import TinderForRestaurantsPage from './screens/public/TinderForRestaurantsPage';
import PickARestaurantPage from './screens/public/PickARestaurantPage';
import RestaurantRoulettePage from './screens/public/RestaurantRoulettePage';
import LunchSpotFinderPage from './screens/public/LunchSpotFinderPage';
import DinnerDecisionAppPage from './screens/public/DinnerDecisionAppPage';
import CantDecideWhatToEatPage from './screens/public/CantDecideWhatToEatPage';
import RestaurantVotingAppPage from './screens/public/RestaurantVotingAppPage';
import MoodBasedRestaurantFinderPage from './screens/public/MoodBasedRestaurantFinderPage';
import BlogPage from './screens/public/BlogPage';
import BlogPostPage from './screens/public/BlogPostPage';

// Auth screens
import LoginScreen from './screens/auth/LoginScreen';
import RegisterScreen from './screens/auth/RegisterScreen';
import ForgotPasswordScreen from './screens/auth/ForgotPasswordScreen';

// Main screens
import HomeScreen from './screens/main/HomeScreen';
import PreferencesScreen from './screens/main/PreferencesScreen';
import SmartSearchScreen from './screens/main/SmartSearchScreen';
import SessionScreen from './screens/main/SessionScreen';
import ResultsScreen from './screens/main/ResultsScreen';
import ProfileScreen from './screens/main/ProfileScreen';
import AnalyticsScreen from './screens/main/AnalyticsScreen';
import CoupleLinkScreen from './screens/main/CoupleLinkScreen';
import SubscriptionScreen from './screens/main/SubscriptionScreen';
import SubscriptionSuccess from './screens/main/SubscriptionSuccess';
import WaitingRoomScreen from './screens/main/WaitingRoomScreen';
import JoinRedirectScreen from './screens/main/JoinRedirectScreen';
import FavoritesScreen from './screens/main/FavoritesScreen';
import CustomListsScreen from './screens/main/CustomListsScreen';
import CustomListDetailScreen from './screens/main/CustomListDetailScreen';
import RewardsScreen from './screens/main/RewardsScreen';
import RedeemScreen from './screens/main/RedeemScreen';
import InviteScreen from './screens/main/InviteScreen';
import VisitConfirmationScreen from './screens/main/VisitConfirmationScreen';
import CashbackAdminScreen from './screens/admin/CashbackAdminScreen';

// Loading screen
import LoadingScreen from './screens/LoadingScreen';
import ProtectedRoute from './components/ProtectedRoute';

function AppNavigator() {
  const { user, hasSession, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Public routes - accessible to everyone */}
      <Route path="/" element={(user || hasSession) ? <Navigate to="/home" replace /> : <LandingPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* SEO pages - keyword targeted */}
      <Route path="/where-should-i-eat" element={<WhereShouldIEatPage />} />
      <Route path="/couples-restaurant-picker" element={<CouplesRestaurantPickerPage />} />
      <Route path="/group-restaurant-decision" element={<GroupRestaurantDecisionPage />} />
      <Route path="/restaurant-wheel-alternative" element={<RestaurantWheelAlternativePage />} />
      <Route path="/food-decision-fatigue" element={<FoodDecisionFatiguePage />} />
      <Route path="/random-restaurant-picker" element={<RandomRestaurantPickerPage />} />
      <Route path="/date-night-restaurants" element={<DateNightRestaurantsPage />} />
      <Route path="/best-restaurants-near-me" element={<BestRestaurantsNearMePage />} />
      <Route path="/what-to-eat-tonight" element={<WhatToEatTonightPage />} />
      <Route path="/try" element={<TryItFreePage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/tinder-for-restaurants" element={<TinderForRestaurantsPage />} />
      <Route path="/pick-a-restaurant-for-me" element={<PickARestaurantPage />} />
      <Route path="/restaurant-roulette" element={<RestaurantRoulettePage />} />
      <Route path="/lunch-spot-finder" element={<LunchSpotFinderPage />} />
      <Route path="/dinner-decision-app" element={<DinnerDecisionAppPage />} />
      <Route path="/cant-decide-what-to-eat" element={<CantDecideWhatToEatPage />} />
      <Route path="/restaurant-voting-app" element={<RestaurantVotingAppPage />} />
      <Route path="/mood-based-restaurant-finder" element={<MoodBasedRestaurantFinderPage />} />

      {/* Blog */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      
      {/* SEO pages - city and cuisine specific */}
      <Route path="/locations" element={<LocationsPage />} />
      <Route path="/city/:citySlug" element={<CityPage />} />
      <Route path="/cuisine/:cuisineSlug" element={<CuisinePage />} />
      
      {/* Auth routes */}
      <Route path="/login" element={(user || hasSession) ? <Navigate to="/home" replace /> : <LoginScreen />} />
      <Route path="/register" element={(user || hasSession) ? <Navigate to="/home" replace /> : <RegisterScreen />} />
      <Route path="/forgot-password" element={(user || hasSession) ? <Navigate to="/home" replace /> : <ForgotPasswordScreen />} />
      
      {/* Core flow - accessible to guests (solo mode) and authenticated users */}
      <Route path="/home" element={<HomeScreen />} />
      <Route path="/preferences" element={<PreferencesScreen />} />
      <Route path="/smart-search" element={<SmartSearchScreen />} />
      <Route path="/session/:code" element={<SessionScreen />} />
      <Route path="/results/:code" element={<ResultsScreen />} />
      <Route path="/join/:code" element={<JoinRedirectScreen />} />

      {/* Protected routes - require authentication */}
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionScreen /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/subscription/cancel" element={<Navigate to="/subscription" replace />} />
      
      <Route path="/profile" element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsScreen /></ProtectedRoute>} />
      <Route path="/couple-link" element={<ProtectedRoute><CoupleLinkScreen /></ProtectedRoute>} />
      <Route path="/waiting-room" element={<WaitingRoomScreen />} />
      <Route path="/favorites" element={<ProtectedRoute><FavoritesScreen /></ProtectedRoute>} />
      <Route path="/lists" element={<ProtectedRoute><CustomListsScreen /></ProtectedRoute>} />
      <Route path="/lists/:listId" element={<ProtectedRoute><CustomListDetailScreen /></ProtectedRoute>} />
      <Route path="/rewards" element={<ProtectedRoute><RewardsScreen /></ProtectedRoute>} />
      <Route path="/redeem" element={<ProtectedRoute><RedeemScreen /></ProtectedRoute>} />
      <Route path="/invite" element={<ProtectedRoute><InviteScreen /></ProtectedRoute>} />
      <Route path="/visit-confirmation" element={<ProtectedRoute><VisitConfirmationScreen /></ProtectedRoute>} />
      <Route path="/admin/cashback" element={<ProtectedRoute><CashbackAdminScreen /></ProtectedRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <AppNavigator />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
